import React, { Component } from "react";
import "../../App.css";
import { getWatchlist, addWatchlist, updateWatchlist, deleteWatchlist } from "../../Api/Http";
import watchlistConfig from "../../config/watchlist.json";
import defaultConfig from "../../config/default.json";
import { sort, executeFunction } from "../../Utils/utils";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Popup from "reactjs-popup";
import DeleteIcon from "../Utils/Svg/delete";
import "reactjs-popup/dist/index.css";

const toNumber = value => (value === null || value === undefined ? null : parseFloat(value, 10));

const display = value => (value === null || value === undefined ? "-" : value);

// Trigger prices, distances and hit counts are computed by the backend; this only maps them for display / sorting.
const mapWatchlist = value => ({
    tradingsymbol: value.tradingsymbol,
    currentPrice: toNumber(value.currentPrice),
    dyChange: toNumber(value.dyChange),
    bullPrice: toNumber(value.bullPrice),
    bullAwayPercent: toNumber(value.bullAwayPercent),
    bearPrice: toNumber(value.bearPrice),
    bearAwayPercent: toNumber(value.bearAwayPercent),
    afterPercentBull: toNumber(value.afterPercentBull),
    distancePercentBull: toNumber(value.distancePercentBull),
    afterPercentBear: toNumber(value.afterPercentBear),
    distancePercentBear: toNumber(value.distancePercentBear),
    consecutiveHitBull: value.consecutiveHitBull,
    consecutiveHitBear: value.consecutiveHitBear,
    bullHistory: value.bullHistory || [],
    bearHistory: value.bearHistory || [],
});

const setDefaultVisibilityOfColumns = () => {
    const obj = {};
    for (const iterator of Object.keys(watchlistConfig.page.columns)) {
        obj[iterator] = {};
        for (const orderIterator of Object.keys(defaultConfig.order.type)) {
            obj[iterator][orderIterator] = defaultConfig.order.type.ascending === orderIterator;
        }
    }
    return obj;
};

class Watchlist extends Component {
    state = {
        watchlist: [],
        updatedValues: {},
        newTradingsymbol: "",
        expanded: null,
        visibility: setDefaultVisibilityOfColumns(),
    };

    async componentDidMount() {
        const resp = await getWatchlist();
        this.setState({ watchlist: (resp || []).map(mapWatchlist) });
    }

    replaceRow(resp) {
        if (resp && resp.tradingsymbol) {
            this.setState(prev => ({
                watchlist: prev.watchlist.map(value =>
                    value.tradingsymbol === resp.tradingsymbol ? mapWatchlist(resp) : value,
                ),
            }));
        }
    }

    ordering = ({ key, type }) => {
        const visibility = this.state.visibility;
        const opposite =
            type === defaultConfig.order.type.ascending
                ? defaultConfig.order.type.descending
                : defaultConfig.order.type.ascending;
        visibility[key][opposite] = true;
        visibility[key][type] = !visibility[key][type];
        this.setState({ watchlist: sort({ data: [...this.state.watchlist], key, type }), visibility });
    };

    onClickOfAdd = async () => {
        // The backend only accepts the exact upper case symbol (e.g. ITC).
        const tradingsymbol = this.state.newTradingsymbol.trim().toUpperCase();
        if (!tradingsymbol) {
            return;
        }
        const resp = await executeFunction({ reference: addWatchlist, body: { tradingsymbol } });
        if (resp && resp.tradingsymbol) {
            this.setState(prev => ({ watchlist: [...prev.watchlist, mapWatchlist(resp)], newTradingsymbol: "" }));
        }
    };

    onClickOfUpdate = async ({ value, body }) => {
        const resp = await executeFunction({
            reference: updateWatchlist,
            body: { tradingsymbol: value.tradingsymbol, ...body },
        });
        this.replaceRow(resp);
    };

    onClickOfUpdatePercent = async ({ value, key }) => {
        const updatedValues = this.state.updatedValues[value.tradingsymbol] || {};
        if (updatedValues[key] === undefined || updatedValues[key] === value[key]) {
            return;
        }
        await this.onClickOfUpdate({ value, body: { [key]: updatedValues[key] } });
        this.setState(prev => {
            const next = { ...prev.updatedValues };
            delete next[value.tradingsymbol];
            return { updatedValues: next };
        });
    };

    onClickOfDelete = async ({ value }) => {
        const resp = await executeFunction({
            reference: deleteWatchlist,
            body: { tradingsymbol: value.tradingsymbol },
        });
        if (resp && resp.tradingsymbol) {
            this.setState(prev => ({
                watchlist: prev.watchlist.filter(row => row.tradingsymbol !== resp.tradingsymbol),
                expanded: prev.expanded === resp.tradingsymbol ? null : prev.expanded,
            }));
        }
    };

    setUpdatedValue({ value, key, newValue }) {
        this.setState(prev => ({
            updatedValues: {
                ...prev.updatedValues,
                [value.tradingsymbol]: { ...(prev.updatedValues[value.tradingsymbol] || {}), [key]: newValue },
            },
        }));
    }

    toggleExpanded = tradingsymbol => {
        this.setState(prev => ({ expanded: prev.expanded === tradingsymbol ? null : tradingsymbol }));
    };

    renderEditablePercent({ value, key }) {
        return (
            <Popup
                key={value.tradingsymbol + "_" + key + "_popup"}
                closeOnDocumentClick
                trigger={<button> {display(value[key])} </button>}
                position="center center"
            >
                <div>
                    <input
                        className="target-input-box"
                        type="number"
                        min="0"
                        step="0.05"
                        defaultValue={value[key] !== null ? value[key] : ""}
                        onChange={event =>
                            this.setUpdatedValue({ value, key, newValue: parseFloat(event.target.value) })
                        }
                    />
                </div>
                <span>
                    <button onClick={() => this.onClickOfUpdatePercent({ value, key })}>&#x2714;</button>
                </span>
            </Popup>
        );
    }

    renderAwayPercent(percent) {
        if (percent === null) {
            return "-";
        }
        // 0 or less: the price has already crossed the trigger.
        return <span className={percent <= 0 ? "value-negative" : ""}>{`${percent}%`}</span>;
    }

    renderHistory({ history, extremeKey, extremeLabel }) {
        if (!history.length) {
            return <span>No hits yet</span>;
        }
        return (
            <table>
                <thead>
                    <tr>
                        {["Trigger Price", "Price", extremeLabel, "Cons Hit", "Next Trigger", "Date"].map(heading => (
                            <th className="triggers-table-columns-heading" key={heading}>
                                {heading}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {history.map(entry => (
                        <tr key={entry.date + entry.triggerPrice}>
                            <td className="triggers-table-rows">{entry.triggerPrice}</td>
                            <td className="triggers-table-rows">{display(entry.price)}</td>
                            <td className="triggers-table-rows">{display(entry[extremeKey])}</td>
                            <td className="triggers-table-rows">{entry.consecutiveHit}</td>
                            <td className="triggers-table-rows">{display(entry.nextTriggerPrice)}</td>
                            <td className="triggers-table-rows">{entry.date || "-"}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    }

    renderHeader() {
        const { columns } = watchlistConfig.page;
        return (
            <tr>
                {Object.keys(columns).map(key => (
                    <th className="triggers-table-columns-heading" key={key}>
                        {columns[key].name}
                        {columns[key].sortable !== false &&
                            Object.keys(defaultConfig.order.type).map(
                                type =>
                                    this.state.visibility[key][type] && (
                                        <button key={type} onClick={() => this.ordering({ key, type })}>
                                            {type === defaultConfig.order.type.ascending ? <>&#9650;</> : <>&#9660;</>}
                                        </button>
                                    ),
                            )}
                    </th>
                ))}
            </tr>
        );
    }

    // e.g. 1695.7(1.14%) / 1695.7(-1.14%); the day change is coloured like the Indices page's Dy Change %.
    renderPrice({ currentPrice, dyChange }) {
        if (currentPrice === null) {
            return "-";
        }
        if (dyChange === null) {
            return currentPrice;
        }
        return (
            <span>
                {currentPrice}
                <span className={dyChange < 0 ? "value-negative" : dyChange > 0 ? "value-positive" : ""}>
                    {`(${dyChange}%)`}
                </span>
            </span>
        );
    }

    // Cells follow watchlist.json's column order, so the header and the row cannot drift apart.
    renderCell({ value, key }) {
        if (key.startsWith("afterPercent") || key.startsWith("distancePercent")) {
            return this.renderEditablePercent({ value, key });
        }
        if (key === "currentPrice") {
            return this.renderPrice(value);
        }
        if (key === "bullAwayPercent" || key === "bearAwayPercent") {
            return this.renderAwayPercent(value[key]);
        }
        if (key === "history") {
            const historyCount = value.bullHistory.length + value.bearHistory.length;
            return (
                <button onClick={() => this.toggleExpanded(value.tradingsymbol)}>
                    {`${this.state.expanded === value.tradingsymbol ? "\u25B2 Hide" : "\u25BC View"} (${historyCount})`}
                </button>
            );
        }
        return display(value[key]);
    }

    renderRow(value) {
        return (
            <tr>
                <td className="triggers-table-rows">
                    <span className="watchlist-symbol">
                        <Popup
                            key={value.tradingsymbol + "_delete_popup"}
                            closeOnDocumentClick
                            trigger={
                                <button
                                    className="watchlist-delete-button"
                                    aria-label={`Delete ${value.tradingsymbol}`}
                                >
                                    <DeleteIcon />
                                </button>
                            }
                            position="right center"
                        >
                            {close => (
                                <div>
                                    <span>Delete {value.tradingsymbol} with its trigger history?</span>
                                    <button
                                        onClick={async () => {
                                            await this.onClickOfDelete({ value });
                                            close();
                                        }}
                                    >
                                        &#x2714;
                                    </button>
                                </div>
                            )}
                        </Popup>
                        {value.tradingsymbol}
                    </span>
                </td>
                {Object.keys(watchlistConfig.page.columns)
                    .filter(key => key !== "tradingsymbol")
                    .map(key => (
                        <td className="triggers-table-rows" key={key}>
                            {this.renderCell({ value, key })}
                        </td>
                    ))}
            </tr>
        );
    }

    render() {
        const columnCount = Object.keys(watchlistConfig.page.columns).length;
        return (
            <div>
                <div>
                    <header className="header-with-tagline">
                        <h1>Watchlist ({this.state.watchlist.length})</h1>
                        <span className="market-header-tagline">
                            Bull Away % is how far the price must rise to the bull price, Bear Away % how far it must
                            fall to the bear price.
                        </span>
                    </header>
                    <div className="watchlist-add">
                        <input
                            className="auth-token-input-box"
                            type="text"
                            placeholder="Add symbol, e.g. ITC"
                            value={this.state.newTradingsymbol}
                            onChange={event => this.setState({ newTradingsymbol: event.target.value })}
                            onKeyDown={event => event.key === "Enter" && this.onClickOfAdd()}
                        />
                        <button onClick={this.onClickOfAdd}>Add</button>
                    </div>
                </div>
                <div>
                    <header className="triggers-table">
                        <table>
                            <thead>{this.renderHeader()}</thead>
                            <tbody>
                                {this.state.watchlist.map(value => (
                                    <React.Fragment key={value.tradingsymbol}>
                                        {this.renderRow(value)}
                                        {this.state.expanded === value.tradingsymbol && (
                                            <tr>
                                                <td className="triggers-table-rows" colSpan={columnCount}>
                                                    <div className="index-observer-history">
                                                        <h4>Sell side (bull price hits)</h4>
                                                        {this.renderHistory({
                                                            history: value.bullHistory,
                                                            extremeKey: "high",
                                                            extremeLabel: "Day High",
                                                        })}
                                                        <h4>Buy side (bear price hits)</h4>
                                                        {this.renderHistory({
                                                            history: value.bearHistory,
                                                            extremeKey: "low",
                                                            extremeLabel: "Day Low",
                                                        })}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </header>
                </div>
                <ToastContainer />
            </div>
        );
    }
}

export default Watchlist;
